// Atlas Map Quiz — POC / mockup.
//
// Known limitations (explicitly out of scope per brief):
//   - Not wired into Course → Unit → Lesson hierarchy.
//   - No DB persistence, no scoring history, no teacher dashboards.
//   - All quiz state is client-side; a page refresh resets it.
//   - Mobile/tablet layouts below the `lg:` breakpoint are NOT supported;
//     the two-column map/panel layout assumes a desktop viewport ≥ 1280px.

import { useMemo, useReducer, useCallback } from 'react';
import { Link } from '@inertiajs/react';
import { cn } from '../../lib/cn';
import ThemeToggle from '../../components/ui/ThemeToggle';

import QuizTopBar from '../../components/atlas/quiz/QuizTopBar';
import QuizMap from '../../components/atlas/quiz/QuizMap';
import QuestionPanel from '../../components/atlas/quiz/QuestionPanel';
import QuizResults from '../../components/atlas/quiz/QuizResults';

import { getQuizBySlug } from '../../data/atlas_quizzes';
import colonyGeo from '../../data/atlas_quizzes/geo/thirteen_colonies.json';
import empireMapData from '../../data/atlas_quizzes/geo/period_2_world_country_map.json';

// -----------------------------------------------------------------------------
// State machine
//
// Per-question state shape:
//   {
//     selectedEntityId:  string | null,   // map_identify
//     selectedOptionId:  string | null,   // map_mcq
//     textResponse:      string,          // map_short_answer
//     selfAssessment:    'got_it' | 'missed' | null,
//     submitted:         boolean,
//     isCorrect:         boolean | null   // null for short_answer
//   }
//
// Top-level reducer state:
//   {
//     currentIndex: number,
//     complete:     boolean,
//     byId:         { [questionId]: perQuestionState }
//   }

function initState(quiz) {
  const byId = {};
  for (const q of quiz.questions) {
    byId[q.id] = freshQuestionState();
  }
  return { currentIndex: 0, complete: false, byId };
}

function freshQuestionState() {
  return {
    selectedEntityId: null,
    selectedOptionId: null,
    textResponse: '',
    selfAssessment: null,
    submitted: false,
    isCorrect: null,
  };
}

function reducer(state, action) {
  switch (action.type) {
    case 'restart':
      return initState(action.quiz);

    case 'select_entity': {
      const qState = state.byId[action.questionId];
      if (!qState || qState.submitted) return state;
      return {
        ...state,
        byId: {
          ...state.byId,
          [action.questionId]: { ...qState, selectedEntityId: action.id },
        },
      };
    }

    case 'select_option': {
      const qState = state.byId[action.questionId];
      if (!qState || qState.submitted) return state;
      return {
        ...state,
        byId: {
          ...state.byId,
          [action.questionId]: { ...qState, selectedOptionId: action.id },
        },
      };
    }

    case 'change_text': {
      const qState = state.byId[action.questionId];
      if (!qState || qState.submitted) return state;
      return {
        ...state,
        byId: {
          ...state.byId,
          [action.questionId]: { ...qState, textResponse: action.text },
        },
      };
    }

    case 'submit': {
      const q = action.question;
      const qState = state.byId[q.id];
      if (!qState || qState.submitted) return state;
      let isCorrect = null;
      if (q.type === 'map_identify' || q.type === 'map_locate') {
        isCorrect = qState.selectedEntityId === q.correct_entity_id;
      } else if (q.type === 'map_mcq') {
        const opt = q.options.find((o) => o.id === qState.selectedOptionId);
        isCorrect = !!opt?.correct;
      }
      return {
        ...state,
        byId: {
          ...state.byId,
          [q.id]: { ...qState, submitted: true, isCorrect },
        },
      };
    }

    case 'self_assess': {
      const qState = state.byId[action.questionId];
      if (!qState || !qState.submitted) return state;
      return {
        ...state,
        byId: {
          ...state.byId,
          [action.questionId]: { ...qState, selfAssessment: action.choice },
        },
      };
    }

    case 'next': {
      const nextIndex = state.currentIndex + 1;
      if (nextIndex >= action.total) {
        return { ...state, complete: true };
      }
      return { ...state, currentIndex: nextIndex };
    }

    default:
      return state;
  }
}

// -----------------------------------------------------------------------------
// Entity label lookup (id → human-readable name), per geo source.

function buildEntityLabels(quiz) {
  if (quiz.geo_source === 'thirteen_colonies') {
    const out = {};
    for (const f of colonyGeo.features) {
      out[f.properties.id] = f.properties.name;
    }
    return out;
  }
  if (quiz.geo_source === 'world_period_2') {
    return { ...empireMapData.labels };
  }
  return {};
}

// -----------------------------------------------------------------------------
// Page

export default function Quiz({ slug }) {
  const quiz = useMemo(() => getQuizBySlug(slug), [slug]);

  if (!quiz) {
    return <QuizNotFound slug={slug} />;
  }

  return <QuizInner quiz={quiz} />;
}

function QuizInner({ quiz }) {
  const [state, dispatch] = useReducer(reducer, quiz, initState);
  const entityLabels = useMemo(() => buildEntityLabels(quiz), [quiz]);

  const question = quiz.questions[state.currentIndex];
  const qState = state.byId[question?.id];
  const total = quiz.questions.length;
  const isLast = state.currentIndex === total - 1;

  const handlePanelAction = useCallback(
    (action) => {
      if (!question) return;
      if (action.type === 'select_option') {
        dispatch({ type: 'select_option', questionId: question.id, id: action.id });
      } else if (action.type === 'change_text') {
        dispatch({ type: 'change_text', questionId: question.id, text: action.text });
      } else if (action.type === 'self_assess') {
        dispatch({ type: 'self_assess', questionId: question.id, choice: action.choice });
      } else if (action.type === 'submit') {
        dispatch({ type: 'submit', question });
      } else if (action.type === 'next') {
        dispatch({ type: 'next', total });
      }
    },
    [question, total],
  );

  const handleEntitySelect = useCallback(
    (id) => {
      if (!question) return;
      if (question.type !== 'map_identify' && question.type !== 'map_locate') return;
      if (qState?.submitted) return;
      dispatch({ type: 'select_entity', questionId: question.id, id });
    },
    [question, qState],
  );

  if (state.complete) {
    return (
      <PageChrome>
        <QuizTopBar
          title={quiz.title}
          courseContext={quiz.course_context}
        />
        <main className="flex-1 overflow-y-auto bg-gray-50 dark:bg-gray-900">
          <QuizResults
            quiz={quiz}
            entityLabels={entityLabels}
            states={state.byId}
            onRestart={() => dispatch({ type: 'restart', quiz })}
          />
        </main>
      </PageChrome>
    );
  }

  // Guard: empty quiz (no questions in fixture / data error).
  if (!question) {
    return (
      <PageChrome>
        <QuizTopBar title={quiz.title} courseContext={quiz.course_context} />
        <main className="flex-1 flex items-center justify-center bg-gray-50 dark:bg-gray-900">
          <p className="text-gray-500 dark:text-gray-400">No questions available.</p>
        </main>
      </PageChrome>
    );
  }

  // Map props per question type.
  const isClickToAnswer =
    question.type === 'map_identify' || question.type === 'map_locate';
  const selectableEntityIds = isClickToAnswer ? question.selectable_entity_ids : null;
  const highlightedEntityIds = isClickToAnswer
    ? null
    : (question.highlighted_entity_ids || []);
  const correctEntityId =
    isClickToAnswer && qState.submitted ? question.correct_entity_id : null;

  return (
    <PageChrome>
      <QuizTopBar
        title={quiz.title}
        courseContext={quiz.course_context}
        questionNumber={state.currentIndex + 1}
        totalQuestions={total}
      />
      <main className="flex-1 grid grid-cols-3 bg-gray-50 dark:bg-gray-900 min-h-0">
        {/* Map column — 2/3 width */}
        <div className="col-span-2 p-4 min-h-0">
          <QuizMap
            geoSource={quiz.geo_source}
            highlightedEntityIds={highlightedEntityIds}
            selectableEntityIds={selectableEntityIds}
            selectedEntityId={isClickToAnswer ? qState.selectedEntityId : null}
            correctEntityId={correctEntityId}
            onEntitySelect={handleEntitySelect}
          />
        </div>

        {/* Panel column — 1/3 width */}
        <aside
          className={cn(
            'col-span-1 flex flex-col',
            'bg-white dark:bg-gray-800',
            'border-l border-gray-200 dark:border-gray-700',
            'p-6 min-h-0',
          )}
        >
          <QuestionPanel
            question={question}
            entityLabels={entityLabels}
            state={qState}
            isLast={isLast}
            onAction={handlePanelAction}
          />
        </aside>
      </main>
    </PageChrome>
  );
}

// Full-viewport chrome shared by all states.
function PageChrome({ children }) {
  return (
    <div className="h-screen flex flex-col bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100">
      <div className="fixed top-3 right-3 z-20">
        <ThemeToggle />
      </div>
      {children}
    </div>
  );
}

function QuizNotFound({ slug }) {
  return (
    <div className="h-screen flex flex-col items-center justify-center bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 px-6">
      <h1 className="text-2xl font-semibold">Quiz not found</h1>
      <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
        No quiz with slug <code className="px-1.5 py-0.5 bg-gray-100 dark:bg-gray-800 rounded">{slug}</code>.
      </p>
      <Link
        href="/atlas"
        className="mt-6 text-sm text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white transition-colors"
      >
        ← Back to Atlas
      </Link>
    </div>
  );
}
